import { apiRequest, } from "../remote/ApiHelper";
import { ApiPaths } from "../remote/ApiPaths";
import { SuburbModel } from "../models/SuburbModel";
import { showLog } from "../helper/utility";
import { SocialMediaLinkModel } from "../models/SocialMediaLinkModel";
import { CompanyInfoModel } from "../models/CompanyInfoModel";
import { BrandModel } from "../models/BrandModel";
import { VehicleTypeModel } from "../models/VehicleTypeModel";
import { StateModel } from "../models/StateModel";
import { BlogModel } from "../models/BlogModel";
import { WebContentSettingsModel } from "../models/WebContentSettingsModel";
import { ModelsModel } from "../models/ModelsModel";
import { HomeModel } from "../models/HomeModel";
import { MobileServicesModel } from "../models/MobileServicesModel";
import { CityModel } from "../models/CityModel";

export const fetchCities = async (stateId?: string,): Promise<{ cities: CityModel[]; }> => {
  const params = stateId ? new URLSearchParams({ state_id: stateId }) : "";

  const response = await apiRequest(
    `${ApiPaths.GET_CITIES()}${params ? `?${params.toString()}` : ""}`,
    "GET", undefined, false, false
  );

  if (response.success) {
    return {
      cities: response.data.records,
    };
  } else {
    showLog("Failed to fetch cities:", response.message);
    return {
      cities: [],
    };
  }
};

export const fetchSuburb = async (cityId?: string, stateId?: string): Promise<{ suburbs: SuburbModel[]; }> => {
  const params = new URLSearchParams({
    ...(stateId && { state_id: stateId }),
    ...(cityId && { city_id: cityId }),
  });

  const response = await apiRequest(
    `${ApiPaths.GET_SUBURB()}?${params.toString()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      suburbs: response.data.records,
    };
  } else {
    showLog("Failed to fetch suburbs:", response.message);
    return {
      suburbs: [],
    };
  }
};

export const fetchSocialMediaLink = async (): Promise<{ socialMediaLinks: SocialMediaLinkModel[]; }> => {
  const response = await apiRequest(
    `${ApiPaths.GET_SOCIAL_MEDIA_LINK()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      socialMediaLinks: response.data.records,
    };
  } else {
    showLog("Failed to fetch social media link:", response.message);
    return {
      socialMediaLinks: [],
    };
  }
};

export const fetchCompanyInfo = async (): Promise<{ companyInfos: CompanyInfoModel[]; }> => {
  const response = await apiRequest(
    `${ApiPaths.GET_COMPANY_INFO()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      companyInfos: response.data.records,
    };
  } else {
    showLog("Failed to fetch company info:", response.message);
    return {
      companyInfos: [],
    };
  }
};

export const fetchVehicle = async (): Promise<{ vehicleTypes: VehicleTypeModel[]; }> => {
  const response = await apiRequest(
    `${ApiPaths.GET_VEHICLE()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      vehicleTypes: response.data.records,
    };
  } else {
    showLog("Failed to fetch vehicles:", response.message);
    return {
      vehicleTypes: [],
    };
  }
};

export const fetchState = async (): Promise<{ states: StateModel[]; }> => {
  const response = await apiRequest(
    `${ApiPaths.GET_STATE()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      states: response.data.records,
    };
  } else {
    showLog("Failed to fetch states:", response.message);
    return {
      states: [],
    };
  }
};

export const fetchWebContentSetting = async (type: number): Promise<{ response: boolean, webContentSettings: WebContentSettingsModel[]; }> => {
  const params = new URLSearchParams({ type: String(type), });
  const response = await apiRequest(
    `${ApiPaths.GET_WEB_CONTENT_SETTINGS()}${params ? `?${params.toString()}` : ""}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      response: true,
      webContentSettings: response.data.records,
    };
  } else {
    showLog("Failed to fetch web content setting:", response.message);
    return {
      response: false,
      webContentSettings: [],
    };
  }
};

export const fetchBrand = async (type?: number): Promise<{ response: boolean, brands: BrandModel[]; }> => {
  const params = new URLSearchParams();
  if (type) params.append("type", String(type));

  const response = await apiRequest(
    `${ApiPaths.GET_BRANDS()}?${params.toString()}`,
    "GET", undefined, false, false
  );

  if (response.success) {
    return {
      response: true,
      brands: response.data.records,
    };
  } else {
    showLog("Failed to fetch brands:", response.message);
    return {
      response: false,
      brands: [],
    };
  }
};

export const fetchModels = async (type?: number): Promise<{ response: boolean, models: ModelsModel[]; }> => {
  const params = new URLSearchParams();
  if (type) params.append("type", String(type));

  const response = await apiRequest(
    `${ApiPaths.GET_MODELS()}?${params.toString()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      response: true,
      models: response.data.records,
    };
  } else {
    showLog("Failed to fetch models:", response.message);
    return {
      response: false,
      models: [],
    };
  }
};

export const fetchBlog = async (
  page: number,
  pageSize: number,
  showLoading: boolean = true,
): Promise<{ response: boolean, blogs: BlogModel[], totalPages: number, totalItems: number }> => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(pageSize),
  });
  const response = await apiRequest(
    `${ApiPaths.GET_BLOGS}?${params.toString()}`,
    "GET", undefined, false, showLoading
  );
  if (response.success) {
    return {
      response: true,
      blogs: response.data.records,
      totalPages: response.data.totalPages,
      totalItems: response.data.totalItems,
    };
  } else {
    showLog("Failed to fetch blogs:", response.message);
    return {
      response: false,
      blogs: [],
      totalPages: 0,
      totalItems: 0,
    };
  }
};

export const fetchBlogDetail = async (slug: string): Promise<{ responseBlogDetail: boolean, blog: BlogModel | null; }> => {
  const response = await apiRequest(`${ApiPaths.GET_BLOG_BY_SLUG()}/${slug}`, "GET");
  if (response.success) {
    return {
      responseBlogDetail: true,
      blog: response.data.record,
    };
  } else {
    return {
      responseBlogDetail: false,
      blog: null,
    };
  }
};

export const fetchHomeData = async (city_id?: string): Promise<{
  response: boolean, homeData: HomeModel | null; selected_city: {
    callout_price: number,
    mobile_fitting_price: number,
    store_price: number,
    delivery_price: number,
  }
}> => {
  const params = new URLSearchParams({
    ...(city_id && { city_id: city_id }),
  });
  const response = await apiRequest(
    `${ApiPaths.GET_HOME}?${params.toString()}`,
    "GET"
  );
  if (response.success) {
    return {
      response: true,
      homeData: response.data.record,
      selected_city: {
        callout_price: response.data.record.selected_city.callout_price,
        mobile_fitting_price: response.data.record.selected_city.fitting_price,
        store_price: response.data.record.selected_city.store_price,
        delivery_price: response.data.record.selected_city.delivery_price,
      },
    };
  } else {
    showLog("Failed to fetch home:", response.message);
    return {
      response: false,
      homeData: null,
      selected_city: {
        callout_price: 0,
        mobile_fitting_price: 0,
        store_price: 0,
        delivery_price: 0,
      },
    };
  }
};

export const fetchMobileService = async (type: string): Promise<{ response: boolean, mobileService: MobileServicesModel | null; }> => {
  const params = new URLSearchParams({ type: type });
  const response = await apiRequest(
    `${ApiPaths.GET_MOBILE_SERVICE}?${params.toString()}`,
    "GET", undefined, false, false
  );
  if (response.success) {
    return {
      response: true,
      mobileService: response.data.records[0],
    };
  } else {
    showLog("Failed to fetch mobile service:", response.message);
    return {
      response: false,
      mobileService: null,
    };
  }
};
